import React, { useState, useEffect } from 'react';
import { getAuthToken } from '../services/api';
import { 
  ClockIcon, 
  UserIcon, 
  FunnelIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

interface ActivityLog {
  logId: string;
  userId: string;
  userName: string;
  userRole: string;
  action: string;
  entityType: string;
  entityId: string;
  details: any;
  timestamp: string;
  ipAddress?: string;
}

const SystemActivity: React.FC = () => {
  const [activities, setActivities] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    userId: '',
    action: '',
    entityType: '',
    dateFrom: '',
    dateTo: '',
    search: ''
  });
  const [limit, setLimit] = useState(50);

  useEffect(() => {
    fetchActivities();
  }, []);

  const fetchActivities = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.userId) params.append('userId', filters.userId);
      if (filters.action) params.append('action', filters.action);
      if (filters.entityType) params.append('entityType', filters.entityType);
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);
      
      const response = await axios.get(`${API_URL}/activity-logs?${params.toString()}&limit=${limit}`, {
        headers: { Authorization: `Bearer ${await getAuthToken()}` }
      });
      
      if (response.data.success) {
        setActivities(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching activities:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters({ ...filters, [key]: value });
  };

  const applyFilters = () => {
    fetchActivities();
  };

  const clearFilters = () => {
    setFilters({
      userId: '',
      action: '',
      entityType: '',
      dateFrom: '',
      dateTo: '',
      search: ''
    });
    setTimeout(() => fetchActivities(), 100);
  };

  const getActionBadge = (action: string) => {
    const badges: { [key: string]: string } = {
      create: 'bg-green-100 text-green-800',
      update: 'bg-blue-100 text-blue-800',
      delete: 'bg-red-100 text-red-800',
      login: 'bg-purple-100 text-purple-800',
      logout: 'bg-gray-100 text-gray-800',
      view: 'bg-cyan-100 text-cyan-800'
    };
    return badges[action.toLowerCase()] || 'bg-gray-100 text-gray-800';
  };

  const getEntityIcon = (entityType: string) => {
    const icons: { [key: string]: string } = {
      lead: '👤',
      application: '📄',
      user: '👥',
      communication: '💬',
      stakeholder: '🤝',
      task: '✓'
    };
    return icons[entityType.toLowerCase()] || '📋';
  };

  const filteredActivities = activities.filter(activity => {
    if (!filters.search) return true;
    const searchLower = filters.search.toLowerCase();
    return (
      activity.userName.toLowerCase().includes(searchLower) ||
      activity.action.toLowerCase().includes(searchLower) ||
      activity.entityType.toLowerCase().includes(searchLower) ||
      activity.details?.toString().toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">System Activity Log</h1>
        <p className="text-gray-600">Monitor all user activities and system changes</p>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <FunnelIcon className="h-5 w-5 mr-2" />
            Filters
          </h3>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Show:</label>
              <select
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
                className="input-field py-1 text-sm"
              >
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={250}>250</option>
                <option value={500}>500</option>
                <option value={1000}>1000</option>
              </select>
              <span className="text-sm text-gray-600">records</span>
            </div>
            <button onClick={clearFilters} className="btn-outline text-sm">
              Clear All
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Action Type
            </label>
            <select
              value={filters.action}
              onChange={(e) => handleFilterChange('action', e.target.value)}
              className="input-field"
            >
              <option value="">All Actions</option>
              <option value="create">Create</option>
              <option value="update">Update</option>
              <option value="delete">Delete</option>
              <option value="login">Login</option>
              <option value="logout">Logout</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Entity Type
            </label>
            <select
              value={filters.entityType}
              onChange={(e) => handleFilterChange('entityType', e.target.value)}
              className="input-field"
            >
              <option value="">All Types</option>
              <option value="lead">Leads</option>
              <option value="application">Applications</option>
              <option value="user">Users</option>
              <option value="communication">Communications</option>
              <option value="stakeholder">Stakeholders</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date From
            </label>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              className="input-field"
            />
          </div>
        </div>

        <div className="flex gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Search activities..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="input-field pl-10"
            />
          </div>
          <button onClick={applyFilters} className="btn-primary flex items-center">
            <ArrowPathIcon className="h-5 w-5 mr-2" />
            Apply Filters
          </button>
        </div>
      </div>

      {/* Activity List */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Recent Activities ({filteredActivities.length})
        </h3>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading activities...</p>
          </div>
        ) : filteredActivities.length === 0 ? (
          <div className="text-center py-12">
            <ClockIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No activities found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredActivities.map((activity) => (
              <div
                key={activity.logId}
                className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 flex-1">
                    <div className="text-2xl">{getEntityIcon(activity.entityType)}</div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getActionBadge(activity.action)}`}>
                          {activity.action.toUpperCase()}
                        </span>
                        <span className="text-sm font-medium text-gray-900">
                          {activity.entityType}
                        </span>
                      </div>
                      
                      <div className="flex items-center text-sm text-gray-600 mb-2">
                        <UserIcon className="h-4 w-4 mr-1" />
                        <span className="font-medium">{activity.userName}</span>
                        <span className="mx-2">•</span>
                        <span className="text-xs text-gray-500 capitalize">{activity.userRole}</span>
                      </div>

                      {activity.details && (
                        <div className="text-sm text-gray-700 bg-gray-50 rounded p-2 mt-2">
                          <pre className="whitespace-pre-wrap font-mono text-xs">
                            {typeof activity.details === 'string' 
                              ? activity.details 
                              : JSON.stringify(activity.details, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="text-right ml-4">
                    <div className="flex items-center text-sm text-gray-500">
                      <ClockIcon className="h-4 w-4 mr-1" />
                      {new Date(activity.timestamp).toLocaleString('en-GB', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                    {activity.entityId && (
                      <div className="text-xs text-gray-400 mt-1">
                        ID: {activity.entityId.substring(0, 8)}...
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SystemActivity;
